import React from 'react';
import { useDispatch, useStore } from 'react-redux';
import { DriverRegistrationPortalNoAuthService } from '@gen/driverRegistration/driverRegistrationPortalNoAuth';
import { useLocation } from 'react-router-dom';
import { useSelector } from '../../redux/store';
import {
  getHash,
  getSteps,
  getTranslations,
  isFormUploading,
} from '../../redux/form/formSelectors';
import { checkShowField } from '../../common/fieldConditions';
import { Field } from '../Field';
import { setFormFieldValue, setFormStep } from '../../redux/form/formActions';
import { clearFieldError } from '../../redux/notification/notificationActions';
import {
  autosaveHandleBlur,
  autosaveHandleBlurOnChangedValue,
  autosaveHandleUpdate,
} from '../../redux/autosave/autosaveThunk';
import { postForm } from '../../redux/form/formThunk';
import { Button } from '../Button';
import { IAppState } from '../../redux/types';
import { AutosaveFieldStatus } from '../AutosaveFieldStatus';
import { getDeeplinkScheme } from '../../common/environment';
import { Loader } from '../common/Loader';
import MarkdownWrapper from '../common/MarkdownWrapper';

const isFieldFilled = (
  field: DriverRegistrationPortalNoAuthService.Field
): boolean => {
  const isCheckboxField =
    field.type ===
      DriverRegistrationPortalNoAuthService.FrontendFieldDefinitionType
        .CHECKBOX ||
    field.type ===
      DriverRegistrationPortalNoAuthService.FrontendFieldDefinitionType
        .GENERIC_CHECKBOX;

  if (isCheckboxField) {
    return true;
  }

  const value = field.current_value;

  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'object') {
    return Object.values(value).some((item) => {
      if (item === null || item === undefined) {
        return false;
      }
      if (typeof item === 'string') {
        return item.trim().length > 0;
      }
      if (Array.isArray(item)) {
        return item.length > 0;
      }
      return true;
    });
  }

  return true;
};

export const DriverSignupMinimal: React.FC = () => {
  const dispatch = useDispatch();
  const store = useStore<IAppState>();
  const hash = useSelector(getHash);
  const commonTranslations = useSelector(getTranslations);
  const isUploading = useSelector(isFormUploading);
  const location = useLocation();
  const category = new URLSearchParams(location.search).get('id');

  const steps = useSelector(getSteps);
  const currentStep = steps.find((s) => s.category === category) || steps[0];

  React.useEffect(() => {
    if (currentStep?.id) {
      dispatch(setFormStep(currentStep.id));
    }
  }, [dispatch, currentStep]);

  const fieldAutosaveBlurHandler = (
    config: {
      enabled: boolean;
      always?: boolean;
    },
    field: DriverRegistrationPortalNoAuthService.Field
  ) => {
    if (!config.enabled) {
      return;
    }
    if (config.always) {
      dispatch(autosaveHandleBlur(field));
    } else {
      dispatch(autosaveHandleBlurOnChangedValue());
    }
  };

  const visibleFields = React.useMemo(() => {
    if (!currentStep) {
      return [];
    }
    return currentStep.fields
      .filter((field) => {
        const showField = checkShowField(field, steps);
        const isDocumentField =
          field.type ===
          DriverRegistrationPortalNoAuthService.FrontendFieldDefinitionType
            .DOCUMENT_UPLOAD;
        return showField && !isDocumentField;
      })
      .map((field, index) => ({
        step: currentStep,
        field,
        key:
          field.generic_field_key || `${currentStep.id}-${field.name}-${index}`,
      }));
  }, [currentStep, steps]);

  const mappedFields = visibleFields.map(({ step, field, key }) => {
    return (
      <Field
        fieldDefinition={field}
        commonTranslations={commonTranslations}
        step={step}
        hash={hash}
        updateHandler={({ changed, autosaveEnabled }) => {
          dispatch(setFormFieldValue(step, field, changed));
          dispatch(clearFieldError(key));
          if (autosaveEnabled) {
            dispatch(autosaveHandleUpdate(field, changed));
          }
        }}
        blurHandler={(_e, autosave) =>
          fieldAutosaveBlurHandler(autosave, field)
        }
        key={key}
      />
    );
  });

  const isFormComplete = React.useMemo(
    () =>
      visibleFields.some(({ field }) => field.is_required) &&
      visibleFields
        .filter(({ field }) => field.is_required)
        .every(({ field }) => isFieldFilled(field)),
    [visibleFields]
  );

  const buttonLabel = React.useMemo(() => {
    if (isUploading) {
      return '';
    }
    return isFormComplete
      ? commonTranslations.common_actions.continue
      : commonTranslations.common_actions.save;
  }, [isUploading, commonTranslations, isFormComplete]);

  const submitHandler = React.useCallback(async () => {
    await dispatch(postForm(currentStep, false, currentStep.category));

    const hasFieldValidationErrors =
      store.getState().notifications.fieldErrors.length > 0;
    const hasCommonErrors =
      store.getState().notifications.commonErrors.length > 0;
    if (!hasFieldValidationErrors && !hasCommonErrors) {
      window.location.replace(`${getDeeplinkScheme()}action/closeBrowser`);
    }
  }, [dispatch, currentStep, store]);

  if (!currentStep || visibleFields.length === 0) {
    return (
      <div className="text-left rtl:text-right tablet:text-center tablet:rtl:text-center mx-auto mt-[-12]">
        <Loader
          data-test="app_form_loader"
          textColor="text-green-500"
          id="document_upload_example_loader"
        />
      </div>
    );
  }

  return (
    <div className="pb-20">
      <div className="text-left rtl:text-right tablet:text-center tablet:rtl:text-center mx-auto">
        <AutosaveFieldStatus commonTranslations={commonTranslations} />
        <h1 className="mt-5 font-bold text-2xl">
          <MarkdownWrapper>{currentStep.translations.title}</MarkdownWrapper>
        </h1>
        {currentStep.translations.subtitle && (
          <div className="text-gray-700 text-sm mt-1">
            <MarkdownWrapper>
              {currentStep.translations.subtitle}
            </MarkdownWrapper>
          </div>
        )}
        <div className="mb-10" />
      </div>
      {mappedFields}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-gray-200 pb-4">
        <div className="tablet:max-w-screen-tablet desktop:max-w-xl pl-6 pr-6 mx-auto w-full">
          <Button
            onClick={submitHandler}
            color={isUploading ? 'bg-green-200' : 'bg-green-500'}
            loadingTextColor="text-green-500"
            label={buttonLabel}
            disabled={isUploading}
            loading={isUploading}
            id="driver_signup_minimal_submit"
            fullWidth
          />
        </div>
      </div>
    </div>
  );
};
